import AxiosService from "plugins/TS-lib-utils-public/services/AxiosService";
import { setUrl, getUrl } from 'helpers/urlsBlob';

// #ux-redesign-start: cache negativo de avatars 404.
// Cuando el backend MS-Multimedia no tiene el archivo, devuelve 404. El
// navegador SIEMPRE loguea respuestas HTTP >= 400 a la consola (es un
// comportamiento de DevTools, no se puede silenciar desde JS). Lo que SI
// podemos es no repetir el request — cacheamos en memoria los fileIds
// que ya 404earon. Resultado: 404 una sola vez por sesion (en el primer
// montaje), no en cada re-render / scroll / re-fetch.
//
// Estructura:
//   knownMissing      → Set<string> de fileIds que dieron 404
//   inflightAvatars   → Map<string, Promise> dedupea requests concurrentes
//
// Si el avatar real se sube despues, recargar la pagina lo limpia.
// Si se necesita invalidar manualmente: `MultimediaService.clearAvatarCache()`.
const knownMissing = new Set();
const inflightAvatars = new Map();
// #ux-redesign-end

class MultimediaService extends AxiosService {
  constructor() {
    super("multimedia");
  }

  // #ux-redesign: helper publico para invalidar cuando se sube un avatar nuevo.
  clearAvatarCache = (fileId) => {
    if (fileId) {
      knownMissing.delete(fileId);
      inflightAvatars.delete(fileId);
    } else {
      knownMissing.clear();
      inflightAvatars.clear();
    }
  };

  /**
   * Array buffer to base64
   * @returns base64
   */
   arrayBufferToBase64 = (buffer) => {
    let binary = '';
    let bytes = new Uint8Array(buffer);
    let len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };
  /**
   * Get avatar
   * @param {string} fileId
   */
  getAvatar = async (fileId, thumb = 'small', reload = false) => {
    try {
      const isTrueSet = fileId && fileId !== 'false' && fileId !== false;
      if (!isTrueSet) return null;

      // 1) Hit en blob cache (avatar ya bajado en esta sesion)
      if (!reload) {
        const urlBlob = getUrl(fileId);
        if (urlBlob) return urlBlob;
      } else {
        // #ux-redesign: si pidieron reload, invalidamos negative cache también.
        knownMissing.delete(fileId);
      }

      // 2) Hit en negative cache → no pegar al backend (evita 404 repetido)
      if (knownMissing.has(fileId)) return null;

      // 3) Dedup de requests concurrentes (varios consumidores piden lo mismo
      //    al mismo tiempo → una sola llamada compartida)
      if (inflightAvatars.has(fileId)) {
        return inflightAvatars.get(fileId);
      }

      const url = `/public/pat-files/avatar/${fileId}${
        thumb ? '?thumbnail=' + thumb : ''
      }`;

      const promise = (async () => {
        const response = await this.get(url, {
          responseType: 'arraybuffer',
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            'Cache-Control': reload ? 'no-cache' : 'private, max-age=25920',
          },
        });

        // AxiosService captura excepciones y devuelve error.response/.request.
        // Detectamos 404 (o cualquier 4xx/5xx) por status y marcamos missing.
        const status = response && response.status;
        if (status && status >= 400) {
          knownMissing.add(fileId);
          return null;
        }

        if (response && response.data && response.data.byteLength > 0) {
          const blob = new Blob([response.data], {
            type: response.headers['content-type'],
          });
          return setUrl(fileId, blob);
        }

        // Sin status >= 400 pero tampoco contenido → marcamos missing tambien
        // para no reintentar en cada re-render.
        knownMissing.add(fileId);
        return null;
      })();

      inflightAvatars.set(fileId, promise);
      try {
        return await promise;
      } finally {
        inflightAvatars.delete(fileId);
      }
    } catch (error) {
      // Fallback: marcamos missing por las dudas y devolvemos null
      if (fileId) knownMissing.add(fileId);
      return null;
    }
  };

  /**
   * Delete avatar
   * @param {string} fileId
   */
   deleteAvatar = async (fileId, thumb = null) => {
    const isTrueSet = fileId !== 'false' && fileId !== false;
    console.log(`/public/pat-files/avatar/${fileId}${
      thumb ? '?thumbnail=' + thumb : ''
    }`);
    if (isTrueSet) {
      const response = await this.delete(
        `/public/pat-files/avatar/${fileId}${
          thumb ? '?thumbnail=' + thumb : ''
        }`,
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        }
      );
      if (response.data) {
        return response.data;
      }
    }
    return null;
  };

  /**
   * Get file for download
   * @param {string} fileId
   */
  getFile = async (fileId) => {
    const isTrueSet = fileId !== "false" && fileId !== false;
    if (isTrueSet) {
      const response = await this.get(`/public/pat-files/${fileId}`, {
        responseType: "arraybuffer",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });
      let blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      return URL.createObjectURL(blob);
    }
    return null;
  };

  /**
   * Get file for download
   * @param {string} fileId
   */
  getReportFile = async (fileId) => {
    if (!fileId) {
      return null;
    }

    const response = await this.get(
      `/public/medical-files/${fileId}`,
      {
        responseType: 'arraybuffer',
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      }
    );
    if (response.data && response.data.byteLength > 0) {
      let contentName = response.headers["content-disposition"];
      contentName = contentName.replace(/(attachment; filename=)(\S+)/gi, "$2");

      return {
        name: contentName,
        data: this.arrayBufferToBase64(response.data)
      }
      
    }
  };

  /**
   * Get file's info
   * @param {string} fileId
   */
  getFileInfo = async (fileId) => {
    const isTrueSet = fileId !== "false" && fileId !== false;
    if (isTrueSet) {
      const response = await this.get(`/public/pat-files/${fileId}/meta`, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });
      return response.data;
    }
    return null;
  };
}

export default new MultimediaService();
